import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { companyApi } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { selectHasSuperAdminAccess } from '../../store/slices/authSlice';

/* ─────────────────────────────────────────────────────────────────────────
   CompanyManagement  –  Platform Admins only
   /platform/companies
────────────────────────────────────────────────────────────────────────── */

const TABS = ['ALL', 'CLIENT', 'VENDOR'];

const inputCls =
  'block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

/* ── Shared helpers ────────────────────────────────────────────────────── */
const norm = (s) => (s ?? '').toLowerCase().trim();

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

/* ── SVG Icon components ───────────────────────────────────────────────── */
function IconEdit({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}

function IconDeactivate({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
    </svg>
  );
}

function IconActivate({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

/* ── Role display helper ───────────────────────────────────────────────── */
const ROLE_LABELS = {
  CLIENT_ADMIN:   'Account Admin',
  CLIENT_USER:    'Account User',
  CLIENT_VIEWER:  'Account Viewer',
  VENDOR_ADMIN:   'Vendor Admin',
  VENDOR_USER:    'Vendor User',
  VENDOR_VIEWER:  'Vendor Viewer',
};

/* ══════════════════════════════════════════════════════════════════════════
   CREATE COMPANY MODAL
══════════════════════════════════════════════════════════════════════════ */
function CreateCompanyModal({ onClose, onCreated, existingCompanies = [] }) {
  const qc = useQueryClient();
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const selectedType = watch('type');

  const createMutation = useMutation(companyApi.createCompany, {
    onSuccess: (company) => {
      const c = company.data ?? company;
      toast.success(`Company "${c.companyName}" created!`);
      qc.invalidateQueries(['companies']);
      onCreated({ id: c.id, name: c.companyName, type: c.type });
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to create company'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-900">Create New Company</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit((d) => createMutation.mutate(d))}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4">

          <Field label="Company Name *" error={errors.companyName?.message}>
            <input {...register('companyName', {
              required: 'Required',
              pattern: { value: /^[a-zA-Z ]+$/, message: 'Only letters and spaces allowed' },
              validate: (v) => !existingCompanies.some(c => norm(c.companyName) === norm(v))
                || 'A company with this name already exists',
            })} className={inputCls} placeholder="Acme Corp" />
          </Field>

          <Field label="Type *" error={errors.type?.message}>
            <select {...register('type', { required: 'Required' })} className={inputCls}>
              <option value="">Select type…</option>
              <option value="CLIENT">Account</option>
              <option value="VENDOR">Vendor</option>
            </select>
          </Field>

          {selectedType === 'VENDOR' && (
            <Field label="Vendor Code" error={errors.vendorCode?.message}>
              <input {...register('vendorCode')} className={inputCls} placeholder="e.g. 20001050" />
              <p className="mt-1 text-xs text-gray-400">Salesforce vendor code — used for Quick Assign</p>
            </Field>
          )}

          <Field label="Address" error={errors.address?.message}>
            <input {...register('address')} className={inputCls} placeholder="123 Main St" />
          </Field>
          <Field label="City" error={errors.city?.message}>
            <input {...register('city')} className={inputCls} placeholder="Chicago" />
          </Field>
          <Field label="State / Province" error={errors.state?.message}>
            <input {...register('state')} className={inputCls} placeholder="IL" />
          </Field>
          <Field label="Country" error={errors.country?.message}>
            <input {...register('country')} className={inputCls} placeholder="US" />
          </Field>
          <Field label="Zip / Postal Code" error={errors.zipCode?.message}>
            <input {...register('zipCode')} className={inputCls} placeholder="60601" />
          </Field>

          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={createMutation.isLoading}
              className="px-6 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              {createMutation.isLoading ? 'Creating…' : 'Create Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   EDIT COMPANY MODAL  (Super Admin only)
══════════════════════════════════════════════════════════════════════════ */
function EditCompanyModal({ company, onClose }) {
  const qc = useQueryClient();
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      companyName: company.companyName,
      type:        company.type,
      vendorCode:  company.vendorCode ?? '',
      address:     company.address ?? '',
      city:        company.city ?? '',
      state:       company.state ?? '',
      country:     company.country ?? '',
      zipCode:     company.zipCode ?? '',
    },
  });
  const selectedType = watch('type');

  const updateMutation = useMutation(
    (data) => companyApi.updateCompany(company.id, data),
    {
      onSuccess: () => {
        toast.success('Company updated!');
        qc.invalidateQueries(['companies']);
        onClose();
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to update company'),
    }
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-900">Edit Company</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit((d) => updateMutation.mutate(d))}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4">

          <Field label="Company Name *" error={errors.companyName?.message}>
            <input {...register('companyName', {
              required: 'Required',
              pattern: { value: /^[a-zA-Z ]+$/, message: 'Only letters and spaces allowed' },
            })} className={inputCls} />
          </Field>

          <Field label="Type *" error={errors.type?.message}>
            <select {...register('type', { required: 'Required' })} className={inputCls}>
              <option value="CLIENT">Account</option>
              <option value="VENDOR">Vendor</option>
            </select>
          </Field>

          {selectedType === 'VENDOR' && (
            <Field label="Vendor Code" error={errors.vendorCode?.message}>
              <input {...register('vendorCode')} className={inputCls} placeholder="e.g. 20001050" />
            </Field>
          )}

          <Field label="Address" error={errors.address?.message}>
            <input {...register('address')} className={inputCls} />
          </Field>
          <Field label="City" error={errors.city?.message}>
            <input {...register('city')} className={inputCls} />
          </Field>
          <Field label="State / Province" error={errors.state?.message}>
            <input {...register('state')} className={inputCls} />
          </Field>
          <Field label="Country" error={errors.country?.message}>
            <input {...register('country')} className={inputCls} />
          </Field>
          <Field label="Zip / Postal Code" error={errors.zipCode?.message}>
            <input {...register('zipCode')} className={inputCls} />
          </Field>

          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={updateMutation.isLoading}
              className="px-6 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              {updateMutation.isLoading ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   CREATE USER MODAL  (renamed from Seed Admin, phone added, all mandatory)
══════════════════════════════════════════════════════════════════════════ */
function CreateUserModal({ companyId, companyName, companyType, onClose, onCreated }) {
  const qc = useQueryClient();
  const { register, handleSubmit, formState: { errors } } = useForm();

  // CLIENT_VENDOR_ADMIN has full parity with Super Admin here, so the role
  // list is the same for every actor who can reach this modal (ADMIN
  // selections route to the seed-admin endpoint below).
  const roleOptions = companyType === 'CLIENT'
    ? [
        { value: 'CLIENT_ADMIN',  label: 'Account Admin' },
        { value: 'CLIENT_USER',   label: 'Account User' },
        { value: 'CLIENT_VIEWER', label: 'Account Viewer' },
      ]
    : [
        { value: 'VENDOR_ADMIN',  label: 'Vendor Admin' },
        { value: 'VENDOR_USER',   label: 'Vendor User' },
        { value: 'VENDOR_VIEWER', label: 'Vendor Viewer' },
      ];

  const ADMIN_ROLES = new Set(['CLIENT_ADMIN', 'VENDOR_ADMIN']);

  const createMutation = useMutation(
    (data) => ADMIN_ROLES.has(data.role)
      ? companyApi.seedCompanyAdmin(companyId, data)
      : companyApi.createCompanyUser(companyId, data),
    {
      onSuccess: () => {
        toast.success('User created!');
        qc.invalidateQueries(['companies']);
        qc.invalidateQueries(['company-users', companyId]);
        onCreated?.();
        onClose();
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to create user'),
    }
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Create User</h2>
            <p className="text-sm text-gray-500 mt-0.5">{companyName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit((d) => createMutation.mutate(d))}
          className="space-y-4">

          <div className="grid grid-cols-2 gap-3">
            <Field label="First Name *" error={errors.firstName?.message}>
              <input {...register('firstName', {
                required: 'Required',
                pattern: { value: /^[a-zA-Z ]+$/, message: 'Letters only' },
              })} className={inputCls} placeholder="Jane" />
            </Field>
            <Field label="Last Name *" error={errors.lastName?.message}>
              <input {...register('lastName', {
                required: 'Required',
                pattern: { value: /^[a-zA-Z ]+$/, message: 'Letters only' },
              })} className={inputCls} placeholder="Smith" />
            </Field>
          </div>

          <Field label="Email *" error={errors.email?.message}>
            <input {...register('email', { required: 'Required' })}
              type="email" className={inputCls} placeholder="user@company.com" />
          </Field>

          <Field label="Phone *" error={errors.phone?.message}>
            <input {...register('phone', { required: 'Required' })}
              className={inputCls} placeholder="+1 555 000 0000" />
          </Field>

          <Field label="Temporary Password *" error={errors.password?.message}>
            <input {...register('password', {
              required: 'Required',
              minLength: { value: 8, message: 'Min 8 characters' },
              pattern: {
                value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
                message: 'Must contain uppercase, lowercase and a number',
              },
            })} type="password" className={inputCls} placeholder="Min. 8 chars, upper + lower + number" />
          </Field>

          <Field label="Role *" error={errors.role?.message}>
            <select {...register('role', { required: 'Required' })} className={inputCls}>
              {roleOptions.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </Field>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={createMutation.isLoading}
              className="px-6 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              {createMutation.isLoading ? 'Creating…' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   EDIT USER MODAL
══════════════════════════════════════════════════════════════════════════ */
function EditUserModal({ user, companyId, companyType, onClose }) {
  const qc = useQueryClient();

  const roleOptions = companyType === 'CLIENT'
    ? [
        { value: 'CLIENT_ADMIN',  label: 'Account Admin' },
        { value: 'CLIENT_USER',   label: 'Account User' },
        { value: 'CLIENT_VIEWER', label: 'Account Viewer' },
      ]
    : [
        { value: 'VENDOR_ADMIN',  label: 'Vendor Admin' },
        { value: 'VENDOR_USER',   label: 'Vendor User' },
        { value: 'VENDOR_VIEWER', label: 'Vendor Viewer' },
      ];

  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      firstName: user.firstName,
      lastName:  user.lastName,
      email:     user.email,
      phone:     user.phone ?? '',
      password:  '',
      role:      user.role ?? '',
    },
  });

  const updateMutation = useMutation(
    (data) => companyApi.updateCompanyUser(companyId, user.id, data),
    {
      onSuccess: () => {
        toast.success('User updated!');
        qc.invalidateQueries(['company-users', companyId]);
        onClose();
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to update user'),
    }
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Edit User</h2>
            <p className="text-sm text-gray-500 mt-0.5">{user.fullName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit((d) => {
          const payload = { ...d };
          if (!payload.password) delete payload.password;
          updateMutation.mutate(payload);
        })} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First Name *" error={errors.firstName?.message}>
              <input {...register('firstName', {
                required: 'Required',
                pattern: { value: /^[a-zA-Z ]+$/, message: 'Letters only' },
              })} className={inputCls} />
            </Field>
            <Field label="Last Name *" error={errors.lastName?.message}>
              <input {...register('lastName', {
                required: 'Required',
                pattern: { value: /^[a-zA-Z ]+$/, message: 'Letters only' },
              })} className={inputCls} />
            </Field>
          </div>

          <Field label="Email *" error={errors.email?.message}>
            <input {...register('email', { required: 'Required' })}
              type="email" className={inputCls} />
          </Field>

          <Field label="Phone *" error={errors.phone?.message}>
            <input {...register('phone', { required: 'Required' })} className={inputCls} />
          </Field>

          <Field label="New Password" error={errors.password?.message}>
            <input {...register('password', {
              minLength: { value: 8, message: 'Min 8 characters' },
            })} type="password" className={inputCls} placeholder="Leave blank to keep current" />
          </Field>

          <Field label="Role *" error={errors.role?.message}>
            <select {...register('role', { required: 'Required' })} className={inputCls}>
              {roleOptions.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </Field>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={updateMutation.isLoading}
              className="px-6 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              {updateMutation.isLoading ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   COMPANY USERS PANEL  (expandable inline sub-table)
══════════════════════════════════════════════════════════════════════════ */
function CompanyUsersPanel({ companyId, companyType, canManage }) {
  const qc = useQueryClient();
  const [editUser, setEditUser] = useState(null);

  const { data, isLoading } = useQuery(
    ['company-users', companyId],
    () => companyApi.getCompanyUsers(companyId, { size: 100 }),
    { staleTime: 30_000 }
  );

  const toggleMutation = useMutation(
    (userId) => companyApi.toggleCompanyUserActive(companyId, userId),
    {
      onSuccess: () => {
        toast.success('User status updated');
        qc.invalidateQueries(['company-users', companyId]);
        qc.invalidateQueries(['companies']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to update status'),
    }
  );

  const users = data?.data?.content ?? data?.content ?? [];

  return (
    <div className="bg-gray-50 border-t border-gray-200 px-4 py-3">
      {isLoading ? (
        <p className="text-xs text-gray-400 py-2">Loading users…</p>
      ) : users.length === 0 ? (
        <p className="text-xs text-gray-400 py-2">No users yet for this company.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-500 uppercase tracking-wide">
              <th className="px-2 py-1.5 text-left font-medium">Name</th>
              <th className="px-2 py-1.5 text-left font-medium">Email</th>
              <th className="px-2 py-1.5 text-left font-medium">Phone</th>
              <th className="px-2 py-1.5 text-left font-medium">Role</th>
              <th className="px-2 py-1.5 text-left font-medium">Status</th>
              {canManage && <th className="px-2 py-1.5 text-left font-medium">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-white transition-colors">
                <td className="px-2 py-2 font-medium text-gray-800">{u.fullName}</td>
                <td className="px-2 py-2 text-gray-600">{u.email}</td>
                <td className="px-2 py-2 text-gray-600">{u.phone || '—'}</td>
                <td className="px-2 py-2">
                  <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">
                    {ROLE_LABELS[u.role] ?? u.role}
                  </span>
                </td>
                <td className="px-2 py-2">
                  <span className={`px-2 py-0.5 rounded-full font-medium ${
                    u.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {u.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                {canManage && (
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditUser(u)}
                        title="Edit user"
                        className="p-1 rounded text-blue-600 hover:bg-blue-50"
                      >
                        <IconEdit />
                      </button>
                      <button
                        onClick={() => toggleMutation.mutate(u.id)}
                        disabled={toggleMutation.isLoading}
                        title={u.active ? 'Deactivate user' : 'Activate user'}
                        className={`p-1 rounded ${
                          u.active
                            ? 'text-red-500 hover:bg-red-50'
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                      >
                        {u.active ? <IconDeactivate /> : <IconActivate />}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editUser && (
        <EditUserModal
          user={editUser}
          companyId={companyId}
          companyType={companyType}
          onClose={() => setEditUser(null)}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   IMPORT VENDORS MODAL  (unchanged)
══════════════════════════════════════════════════════════════════════════ */

const PREVIEW_STATUS_STYLES = {
  VALID:     { row: 'bg-green-50',  badge: 'bg-green-100 text-green-800'  },
  INVALID:   { row: 'bg-red-50',    badge: 'bg-red-100 text-red-800'      },
  DUPLICATE: { row: 'bg-yellow-50', badge: 'bg-yellow-100 text-yellow-800'},
};
const IMPORT_STATUS_STYLES = {
  IMPORTED: 'bg-green-100 text-green-800',
  SKIPPED:  'bg-yellow-100 text-yellow-800',
  FAILED:   'bg-red-100 text-red-800',
};

const PREVIEW_COLS = [
  ['#',           'rowNumber'],
  ['Company',     'companyName'],
  ['Address',     'address'],
  ['City',        'city'],
  ['State',       'state'],
  ['Country',     'country'],
  ['Zip',         'zipCode'],
  ['Vendor Code', 'vendorCode'],
  ['Status',      '_status'],
  ['Note',        'message'],
];

function ImportVendorsModal({ onClose }) {
  const [step, setStep]         = useState('upload');
  const [file, setFile]         = useState(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview]   = useState(null);
  const [result, setResult]     = useState(null);
  const fileInputRef            = useRef();

  const previewMutation = useMutation(companyApi.previewImport, {
    onSuccess: (res) => { setPreview(res?.data ?? res); setStep('preview'); },
    onError:   (err) => toast.error(err.response?.data?.error || 'Failed to parse file.'),
  });

  const confirmMutation = useMutation(
    () => companyApi.confirmImport(preview.rows),
    {
      onSuccess: (res) => {
        const data = res?.data ?? res;
        setResult(data); setStep('done');
        toast.success(`Import complete — ${data.imported} vendor(s) saved.`);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Confirm failed.'),
    }
  );

  const validateAndSet = (f) => {
    if (!f.name.endsWith('.xlsx') && !f.name.endsWith('.xls')) {
      toast.error('Only .xlsx or .xls files are accepted.'); return;
    }
    setFile(f);
  };

  const reset = () => {
    setStep('upload'); setFile(null); setPreview(null); setResult(null);
    previewMutation.reset(); confirmMutation.reset();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const stepLabel = step === 'upload' ? 'Upload File' : step === 'preview' ? 'Review Data' : 'Import Complete';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Import Vendors</h2>
            <p className="text-sm text-gray-400 mt-0.5">{stepLabel}</p>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs font-medium">
            {['upload', 'preview', 'done'].map((s, i) => (
              <React.Fragment key={s}>
                {i > 0 && <span className="text-gray-300">›</span>}
                <span className={`px-2.5 py-1 rounded-full ${
                  step === s ? 'bg-violet-600 text-white'
                  : ['preview','done'].includes(step) && s === 'upload' ? 'bg-green-100 text-green-700'
                  : step === 'done' && s === 'preview' ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-400'
                }`}>
                  {s === 'upload' ? '1. Upload' : s === 'preview' ? '2. Review' : '3. Done'}
                </span>
              </React.Fragment>
            ))}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none ml-4">×</button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {step === 'upload' && (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) validateAndSet(f); }}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                dragging ? 'border-violet-400 bg-violet-50' : 'border-gray-300 hover:border-violet-400 hover:bg-violet-50/40'
              }`}
            >
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden"
                onChange={(e) => { if (e.target.files[0]) validateAndSet(e.target.files[0]); }} />
              {file ? (
                <div className="space-y-1">
                  <svg className="mx-auto h-10 w-10 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm font-medium text-gray-700">{file.name}</p>
                  <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-xs text-red-500 hover:underline">Remove</button>
                </div>
              ) : (
                <div className="space-y-2">
                  <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm font-medium text-gray-700">Drop your Excel file here or <span className="text-violet-600">browse</span></p>
                  <p className="text-xs text-gray-400">Accepts .xlsx or .xls</p>
                </div>
              )}
            </div>
          )}

          {step === 'preview' && preview && (
            <div className="space-y-3">
              <div className="flex gap-4 text-sm">
                <span className="px-2.5 py-1 rounded-full bg-green-100 text-green-800 font-medium">{preview.validCount} valid</span>
                <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-800 font-medium">{preview.invalidCount} invalid</span>
                <span className="px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-800 font-medium">{preview.duplicateCount} duplicate</span>
              </div>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>{PREVIEW_COLS.map(([label]) => <th key={label} className="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">{label}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(preview.rows ?? []).map((row, i) => {
                      const s = PREVIEW_STATUS_STYLES[row._status] ?? {};
                      return (
                        <tr key={i} className={s.row ?? ''}>
                          {PREVIEW_COLS.map(([, field]) => (
                            <td key={field} className="px-3 py-2 whitespace-nowrap">
                              {field === '_status'
                                ? <span className={`px-2 py-0.5 rounded-full font-medium ${s.badge ?? ''}`}>{row._status}</span>
                                : (row[field] ?? '—')}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {preview.invalidCount > 0 && <p className="text-xs text-red-500">⚠ Rows marked INVALID will be skipped. Fix the file and re-upload to include them.</p>}
            </div>
          )}

          {step === 'done' && result && (
            <div className="text-center space-y-4 py-6">
              <div className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">Import Complete</p>
                <p className="text-sm text-gray-500 mt-1">{result.imported} imported · {result.skipped} skipped · {result.failed} failed</p>
              </div>
              {(result.rows ?? []).length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-gray-200 text-left">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>{['Company', 'Status', 'Note'].map(h => <th key={h} className="px-3 py-2 text-left font-medium text-gray-500">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {result.rows.map((row, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2">{row.companyName}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full font-medium ${IMPORT_STATUS_STYLES[row.status] ?? ''}`}>{row.status}</span>
                          </td>
                          <td className="px-3 py-2 text-gray-500">{row.message ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={step === 'done' ? onClose : reset}
            className="text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg px-4 py-2">
            {step === 'done' ? 'Close' : 'Cancel'}
          </button>
          <div className="flex gap-3">
            {step === 'upload' && (
              <button
                onClick={() => { if (file) previewMutation.mutate(file); }}
                disabled={!file || previewMutation.isLoading}
                className="px-5 py-2 bg-violet-600 text-white text-sm rounded-lg font-medium hover:bg-violet-700 disabled:opacity-40 flex items-center gap-2"
              >
                {previewMutation.isLoading ? 'Parsing…' : 'Preview Import'}
              </button>
            )}
            {step === 'preview' && (
              <>
                <button onClick={() => { setStep('upload'); setPreview(null); previewMutation.reset(); }}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">← Back</button>
                <button
                  onClick={() => confirmMutation.mutate()}
                  disabled={!preview?.validCount || confirmMutation.isLoading}
                  className="px-5 py-2 bg-violet-600 text-white text-sm rounded-lg font-medium hover:bg-violet-700 disabled:opacity-40"
                >
                  {confirmMutation.isLoading ? 'Importing…' : `Confirm Import (${preview?.validCount ?? 0})`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════════════════════ */
export default function CompanyManagement() {
  const canManageCompanies = useSelector(selectHasSuperAdminAccess);
  const qc           = useQueryClient();

  const [activeTab,       setActiveTab]       = useState('ALL');
  const [showCreate,      setShowCreate]       = useState(false);
  const [showImport,      setShowImport]       = useState(false);
  const [createUserFor,   setCreateUserFor]    = useState(null);  // { id, name, type }
  const [editCompany,     setEditCompany]      = useState(null);  // company object
  const [expandedId,      setExpandedId]       = useState(null);  // companyId | null

  const { data, isLoading } = useQuery(
    ['companies', activeTab],
    () => activeTab === 'ALL'
      ? companyApi.getAllCompanies({ size: 200 })
      : companyApi.getAllCompanies({ type: activeTab, size: 200 }),
    { keepPreviousData: true }
  );

  const toggleCompanyMutation = useMutation(
    (id) => companyApi.toggleCompanyActive(id),
    {
      onSuccess: () => { toast.success('Company status updated'); qc.invalidateQueries(['companies']); },
      onError:   (err) => toast.error(err.response?.data?.error || 'Failed to toggle'),
    }
  );

  const companies = data?.data?.content ?? data?.content ?? [];
  const TABS_DISPLAY = { ALL: 'All', CLIENT: 'Accounts', VENDOR: 'Vendors' };

  const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id);

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage Accounts and Vendors</p>
        </div>
        <div className="flex gap-2">
          {canManageCompanies && (
            <>
              <button
                onClick={() => companyApi.downloadVendorTemplate()}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Template
              </button>
              <button
                onClick={() => setShowImport(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l4-4m0 0l4 4m-4-4v12" />
                </svg>
                Import Vendors
              </button>
            </>
          )}
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg font-medium"
          >
            + New Company
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              activeTab === tab ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}>
            {TABS_DISPLAY[tab]}
          </button>
        ))}
      </div>

      {/* ── Company table ── */}
      {isLoading ? <LoadingSpinner /> : companies.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
          <p className="text-gray-400 text-sm">No companies found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Company', 'Type', 'Vendor Code', 'Users', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {companies.map(c => (
                <React.Fragment key={c.id}>
                  {/* ── Company row ── */}
                  <tr className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${expandedId === c.id ? 'bg-blue-50/30' : ''}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{c.companyName}</td>

                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        c.type === 'CLIENT' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {c.type === 'CLIENT' ? 'Account' : 'Vendor'}
                      </span>
                    </td>

                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{c.vendorCode || '—'}</td>

                    {/* Users count + expand toggle */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium min-w-[28px] text-center">
                          {c.userCount ?? 0}
                        </span>
                        <button
                          onClick={() => toggleExpand(c.id)}
                          title={expandedId === c.id ? 'Collapse users' : 'Show users'}
                          className={`text-gray-400 hover:text-blue-600 transition-transform duration-200 ${expandedId === c.id ? 'rotate-90 text-blue-600' : ''}`}
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        c.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {c.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {/* Create User — available to all platform admins; disabled for inactive companies */}
                        <button
                          onClick={() => setCreateUserFor({ id: c.id, name: c.companyName, type: c.type })}
                          disabled={!c.active}
                          title={!c.active ? 'Company is inactive' : undefined}
                          className="px-2.5 py-1 text-xs border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none"
                        >
                          + Create User
                        </button>

                        {/* Edit & Toggle — Super Admin / CVA only, shown as icon buttons */}
                        {canManageCompanies && (
                          <>
                            <button
                              onClick={() => setEditCompany(c)}
                              title="Edit company"
                              className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                            >
                              <IconEdit />
                            </button>
                            <button
                              onClick={() => toggleCompanyMutation.mutate(c.id)}
                              disabled={toggleCompanyMutation.isLoading}
                              title={c.active ? 'Deactivate company' : 'Activate company'}
                              className={`p-1.5 rounded-lg ${
                                c.active
                                  ? 'text-red-500 hover:bg-red-50'
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                            >
                              {c.active ? <IconDeactivate /> : <IconActivate />}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* ── Expanded users panel ── */}
                  {expandedId === c.id && (
                    <tr className="border-b border-gray-200">
                      <td colSpan={6} className="p-0">
                        <CompanyUsersPanel
                          companyId={c.id}
                          companyType={c.type}
                          canManage={canManageCompanies}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modals ── */}
      {showCreate && (
        <CreateCompanyModal
          onClose={() => setShowCreate(false)}
          onCreated={(company) => {
            setCreateUserFor(company);
          }}
          existingCompanies={companies}
        />
      )}

      {createUserFor && (
        <CreateUserModal
          companyId={createUserFor.id}
          companyName={createUserFor.name}
          companyType={createUserFor.type}
          onClose={() => setCreateUserFor(null)}
          onCreated={() => {
            setExpandedId(createUserFor.id);
          }}
        />
      )}

      {editCompany && (
        <EditCompanyModal
          company={editCompany}
          onClose={() => setEditCompany(null)}
        />
      )}

      {showImport && <ImportVendorsModal onClose={() => setShowImport(false)} />}
    </div>
  );
}
